import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Search, Sparkles, Plus } from "lucide-react";

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

function UserRow({ u, onChange }) {
  const [cap, setCap] = useState(u.credit_cap || 250);
  const [topup, setTopup] = useState("");
  const [busy, setBusy] = useState(false);

  const setBeta = async (beta) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/beta`, { beta_tester: beta, credit_cap: Number(cap) || 0 });
      toast.success(beta ? `${u.email} now has Pro beta access (${data.credit_cap} credits/mo)` : `Beta access removed for ${u.email}`);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update");
    }
    setBusy(false);
  };

  const saveCap = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/beta`, { beta_tester: true, credit_cap: Number(cap) || 0 });
      setCap(data.credit_cap);
      toast.success(`Cap set to ${data.credit_cap} credits/mo`);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update");
    }
    setBusy(false);
  };

  const addCredits = async () => {
    const amt = Number(topup);
    if (!amt) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/credits`, { amount: amt });
      toast.success(`${u.email} now has ${data.credits_topup} top-up credits`);
      setTopup("");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add credits");
    }
    setBusy(false);
  };

  const capNum = u.beta_tester ? u.credit_cap : 100;
  const usedPct = capNum ? Math.min(100, Math.round((u.used_this_month / capNum) * 100)) : 0;

  return (
    <div
      data-testid={`beta-row-${u.id}`}
      className={`rounded-2xl border p-4 space-y-3 ${
        u.beta_tester ? "border-[#00E5FF]/30 bg-[#0E131F]" : "border-[#1E273D] bg-[#0E131F]"
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-200">{u.name || "—"}</span>
            {u.beta_tester && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#00E5FF] bg-[#0E131F] border border-[#00E5FF]/30 px-1.5 py-0.5 rounded">
                <Sparkles className="w-3 h-3" strokeWidth={2} /> Pro beta
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400">{u.email}</div>
          <div className="text-xs text-slate-500 mt-0.5">Joined {fmtDate(u.created_at)} · last active {fmtDate(u.last_active)}</div>
        </div>
        {u.beta_tester ? (
          <Button
            data-testid={`beta-revoke-${u.id}`}
            onClick={() => setBeta(false)}
            disabled={busy}
            variant="ghost"
            className="rounded-full text-xs text-slate-400 hover:text-rose-400 h-8 px-3"
          >
            Remove beta
          </Button>
        ) : (
          <Button
            data-testid={`beta-grant-${u.id}`}
            onClick={() => setBeta(true)}
            disabled={busy}
            className="rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-xs h-8 px-4"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Grant Pro beta"}
          </Button>
        )}
      </div>

      {/* Usage meter */}
      <div>
        <div className="flex justify-between text-xs text-slate-400 mb-1">
          <span>Used this month</span>
          <span>
            {u.used_this_month} / {capNum} credits · {u.actions_this_month} actions
          </span>
        </div>
        <div className="h-2 rounded-full bg-[#141B29] overflow-hidden">
          <div
            className={`h-full rounded-full ${usedPct >= 90 ? "bg-rose-400" : "bg-[#00E5FF]"}`}
            style={{ width: `${usedPct}%` }}
          />
        </div>
        <div className="text-[11px] text-slate-500 mt-1">
          Balance: {u.credits} monthly + {u.credits_topup} top-up
        </div>
      </div>

      {/* Controls (beta only) */}
      {u.beta_tester && (
        <div className="flex flex-wrap items-end gap-3 pt-1">
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Monthly cap</label>
            <div className="flex items-center gap-2">
              <Input
                data-testid={`beta-cap-${u.id}`}
                type="number"
                value={cap}
                onChange={(e) => setCap(e.target.value)}
                className="w-24 h-8 rounded-lg text-sm"
              />
              <Button
                data-testid={`beta-cap-save-${u.id}`}
                onClick={saveCap}
                disabled={busy}
                variant="outline"
                className="h-8 px-3 rounded-lg text-xs border-[#3A4763]"
              >
                Set
              </Button>
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Top-up credits</label>
            <div className="flex items-center gap-2">
              <Input
                data-testid={`beta-topup-${u.id}`}
                type="number"
                placeholder="e.g. 100"
                value={topup}
                onChange={(e) => setTopup(e.target.value)}
                className="w-28 h-8 rounded-lg text-sm"
              />
              <Button
                data-testid={`beta-topup-add-${u.id}`}
                onClick={addCredits}
                disabled={busy || !topup}
                className="h-8 px-3 rounded-lg text-xs bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E]"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Add
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BetaTesters() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setErr(false);
    try {
      const { data } = await api.get("/admin/beta-testers");
      setUsers(data);
    } catch {
      setErr(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  if (loading)
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-5 h-5 text-[#00E5FF] animate-spin" strokeWidth={1.5} />
      </div>
    );
  if (err)
    return (
      <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6 text-center space-y-3">
        <p className="text-sm text-slate-400">{"Couldn't load members."}</p>
        <button data-testid="beta-retry" onClick={load} className="text-sm text-[#00E5FF] hover:underline">
          Try again
        </button>
      </div>
    );

  const filtered = users.filter(
    (u) =>
      !q ||
      (u.email || "").toLowerCase().includes(q.toLowerCase()) ||
      (u.name || "").toLowerCase().includes(q.toLowerCase())
  );
  const betas = filtered.filter((u) => u.beta_tester);
  const others = filtered.filter((u) => !u.beta_tester);

  return (
    <div data-testid="admin-beta" className="space-y-5">
      <div>
        <h2 className="text-2xl text-white">Beta testers</h2>
        <p className="text-sm text-slate-400 mt-1">
          Grant a member full Pro access (best model + all creation tools) without charging them, set a
          custom monthly credit cap, top them up anytime, and watch their usage. Comped testers are never
          counted as paid revenue.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" strokeWidth={1.5} />
        <Input
          data-testid="beta-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email…"
          className="pl-9 rounded-xl border-[#29344D]"
        />
      </div>

      {betas.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF]">
            Active beta testers ({betas.length})
          </div>
          {betas.map((u) => (
            <UserRow key={u.id} u={u} onChange={load} />
          ))}
        </div>
      )}

      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          All members ({others.length})
        </div>
        {others.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">No members match.</p>
        ) : (
          others.map((u) => <UserRow key={u.id} u={u} onChange={load} />)
        )}
      </div>
    </div>
  );
}
